import { useState } from "react";
import { Download, Smartphone, Clock, Share2, Plus, X } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfig";
import { usePWAInstall } from "@/hooks/usePWAInstall";

// -------------------------------------------------- Brand glyphs
const AndroidGlyph = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17.6 9.48l1.84-3.18a.4.4 0 0 0-.15-.55.4.4 0 0 0-.55.14l-1.86 3.22a11.4 11.4 0 0 0-9.76 0L5.26 5.9a.4.4 0 0 0-.7.4l1.84 3.19A10.86 10.86 0 0 0 1 18h22a10.86 10.86 0 0 0-5.4-8.52ZM7 15.25a1 1 0 1 1 1-1 1 1 0 0 1-1 1Zm10 0a1 1 0 1 1 1-1 1 1 0 0 1-1 1Z" />
  </svg>
);
const AppleGlyph = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M16.36 12.75c0-2.31 1.89-3.42 1.97-3.47-1.07-1.57-2.74-1.79-3.34-1.81-1.42-.15-2.77.84-3.5.84-.72 0-1.83-.82-3.02-.8-1.55.02-2.98.9-3.77 2.29-1.61 2.8-.41 6.95 1.16 9.22.77 1.11 1.68 2.36 2.87 2.31 1.15-.05 1.59-.75 2.98-.75s1.79.75 3.02.72c1.25-.02 2.03-1.13 2.79-2.25.88-1.29 1.24-2.54 1.26-2.6-.03-.01-2.42-.93-2.42-3.7ZM14.3 5.94c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.68 1.37-.59.67-1.11 1.76-.97 2.79 1.02.08 2.07-.51 2.71-1.26Z" />
  </svg>
);
const WindowsGlyph = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M3 5.5 10.7 4.4v7.6H3V5.5Zm0 7.9h7.7V21L3 19.9v-6.5Zm8.6-9.2L21 3v9.1h-9.4V4.2Zm0 9.2H21V21l-9.4-1.4v-6.2Z" />
  </svg>
);
const PwaGlyph = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path d="M12 3l9 4v6c0 5-3.5 8-9 9-5.5-1-9-4-9-9V7l9-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M12 8v6M9 12l3-3 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const AppStoreGlyph = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17 3H7a4 4 0 0 0-4 4v10a4 4 0 0 0 4 4h10a4 4 0 0 0 4-4V7a4 4 0 0 0-4-4Zm-4.7 5.6.4-.7c.2-.4.7-.5 1-.3.4.2.5.7.3 1l-.4.7L15.5 13H17a.75.75 0 0 1 0 1.5h-2.4l.9 1.5a.75.75 0 0 1-1.3.7l-.5-.9c-.5.6-1.3 1-2.2 1a2.8 2.8 0 0 1-2.5-4.3l2.9-4.9-.3-.6c-.2-.4-.1-.8.3-1 .4-.2.8 0 1 .3l.4.6ZM11 15c.4 0 .8-.2 1-.5L10.5 12l-.8 1.3A1.3 1.3 0 0 0 11 15Zm-3-.5c-.4 0-.7.3-.7.7 0 .5.3.7.7.7h.2c.4 0 .7-.2.7-.7v-.1a.7.7 0 0 0-.6-.6H8Z" />
  </svg>
);

// -------------------------------------------------- Button
const Btn = ({ href, onClick, disabled, label, sub, icon, badge, testid }) => {
  const isBtn = !!onClick;
  const disabledCls = disabled
    ? "opacity-60 cursor-not-allowed"
    : "hover:border-[#F5C542]/60 hover:bg-white/[0.06] hover:-translate-y-0.5";
  const inner = (
    <>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {icon}
      </div>
      <div className="text-left flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-bold leading-none">
          {sub}
        </div>
        <div className="mt-0.5 font-display font-bold text-white text-sm whitespace-nowrap">{label}</div>
        {(disabled || badge) && (
          <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider"
               style={{ color: disabled ? "#F5C542" : "#00E676" }}>
            {disabled ? (<><Clock className="w-2.5 h-2.5" /> {badge || "Em breve"}</>) : badge}
          </div>
        )}
      </div>
      {!disabled && !badge && <Download className="w-4 h-4 text-[#F5C542] shrink-0" />}
    </>
  );
  const cls = `group relative flex items-center gap-3 rounded-2xl p-3 pr-4 min-w-0 transition-all duration-300 border ${disabledCls}`;
  const style = { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" };

  if (disabled) return <div className={cls} style={style} data-testid={testid} aria-disabled="true">{inner}</div>;
  if (isBtn)
    return (
      <button onClick={onClick} className={cls + " text-left"} style={style} data-testid={testid} type="button">
        {inner}
      </button>
    );
  return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={testid}>{inner}</a>;
};

// -------------------------------------------------- iOS "Add to Home Screen" modal
const IOSModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md rounded-3xl p-6 text-center relative"
           style={{ background: "linear-gradient(160deg, #141414, #0a0a0a)", border: "1px solid rgba(245, 197, 66,0.3)" }}
           data-testid="pwa-ios-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
        <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center"
             style={{ background: "rgba(245, 197, 66,0.15)", border: "1px solid rgba(245, 197, 66,0.55)" }}>
          <AppleGlyph className="w-7 h-7 text-white" />
        </div>
        <h3 className="mt-4 font-display font-black text-white text-xl">Instalar no iPhone / iPad</h3>
        <div className="mt-4 text-sm text-white/70 leading-relaxed space-y-3 text-left">
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: "rgba(245, 197, 66,0.15)", color: "#F5C542", border: "1px solid rgba(245, 197, 66,0.5)" }}>1</span>
            <div>Abra o site no <b>Safari</b>.</div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: "rgba(245, 197, 66,0.15)", color: "#F5C542", border: "1px solid rgba(245, 197, 66,0.5)" }}>2</span>
            <div>Toque no ícone de <b>Compartilhar</b> <Share2 className="inline w-3.5 h-3.5" /> na barra inferior.</div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: "rgba(245, 197, 66,0.15)", color: "#F5C542", border: "1px solid rgba(245, 197, 66,0.5)" }}>3</span>
            <div>Escolha <b>"Adicionar à Tela de Início"</b> <Plus className="inline w-3.5 h-3.5" /> e confirme.</div>
          </div>
        </div>
        <button onClick={onClose}
          className="mt-6 w-full rounded-full py-3 font-display font-bold uppercase tracking-wider text-white text-sm"
          style={{ background: "linear-gradient(135deg, #F28C28, #F5C542)", boxShadow: "0 10px 30px rgba(245, 197, 66,0.35)" }}>
          Entendi
        </button>
      </div>
    </div>
  );
};

// -------------------------------------------------- Main component
export const AppDownload = () => {
  const cfg = useSiteConfig();
  const pwa = usePWAInstall();
  const [iosOpen, setIosOpen] = useState(false);

  const android = { url: cfg.app_android_url, enabled: cfg.app_android_enabled !== false };
  const win = { url: cfg.app_windows_url, enabled: cfg.app_windows_enabled !== false };
  const mac = { url: cfg.app_macos_url, enabled: cfg.app_macos_enabled !== false };
  const apple = { url: cfg.app_apple_store_url, enabled: cfg.app_apple_store_enabled !== false };
  const pwaEnabled = cfg.app_pwa_enabled !== false;

  const pwaClick = () => {
    if (pwa.isStandalone || pwa.installed) return;
    if (pwa.canInstall) {
      pwa.install();
    } else if (pwa.isIOS) {
      setIosOpen(true);
    } else {
      setIosOpen(true); // fallback: show instructions modal for any device
    }
  };

  const pwaBadge = pwa.installed || pwa.isStandalone ? "Instalado" : null;

  return (
    <section
      className="rounded-3xl p-6 md:p-7 mb-12 relative overflow-hidden"
      style={{
        background: "linear-gradient(140deg, rgba(245, 197, 66,0.06), rgba(242, 140, 40,0.03) 55%, rgba(20,20,20,0.6))",
        border: "1px solid rgba(245, 197, 66,0.22)",
      }}
      data-testid="app-download"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(245, 197, 66,0.18), transparent 60%)" }} />

      <div className="relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
                 style={{ color: "#F5C542", background: "rgba(245, 197, 66,0.10)", border: "1px solid rgba(245, 197, 66,0.4)" }}>
              <Smartphone className="w-3 h-3" /> Aplicativos
            </div>
            <h3 className="mt-3 font-display font-black text-white text-2xl md:text-3xl leading-tight tracking-tight">
              Baixe nosso <span className="text-gold-gradient">Aplicativo</span>
            </h3>
            <p className="mt-2 text-sm text-white/55 max-w-lg leading-relaxed">
              Acesso rápido a sinais, oportunidades e conteúdo — instale no celular, tablet ou computador.
            </p>
          </div>
          {cfg.app_version && (
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Versão atual</div>
              <div className="font-mono font-bold text-white tracking-tight">v{cfg.app_version}</div>
            </div>
          )}
        </div>

        <div className="max-w-md">
          <Btn
            testid="dl-pwa"
            onClick={pwaClick}
            disabled={!pwaEnabled}
            label={pwaBadge ? "App Instalado" : "Instalar App Web"}
            sub="PWA — Navegador"
            badge={pwaBadge}
            icon={<PwaGlyph className="w-5 h-5 text-[#F5C542]" />}
          />
        </div>
      </div>

      <IOSModal open={iosOpen} onClose={() => setIosOpen(false)} />
    </section>
  );
};
